import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { LlmGenerationStreamEvent } from "../../protocol"
import { getActiveClerkToken } from "../utils/activeClerkToken"
import {
  getSofiaLlmGenerationStreamEvents,
  isSofiaLlmGenerationTaskId,
  runSofiaLlmGenerationJob,
} from "../utils/sofiaJobs"
import {
  appendStreamChunkToBuffer,
  applyDefaultLlmGenerationStreamEvent,
  createInitialLlmGenerationStreamState,
  type LlmGenerationClientMessage,
  type LlmGenerationStreamState,
} from "../utils/state"

const MISSING_DONE_EVENT_MESSAGE = "Stream ended before done event"

export interface LlmGenerationStreamReducerArgs<
  TMessage extends LlmGenerationClientMessage,
  TCustom,
> {
  event: LlmGenerationStreamEvent
  state: LlmGenerationStreamState<TMessage, TCustom>
  applyDefault: (
    state: LlmGenerationStreamState<TMessage, TCustom>,
  ) => LlmGenerationStreamState<TMessage, TCustom>
}

interface UseLlmGenerationStreamOptions<
  TInput,
  TMessage extends LlmGenerationClientMessage,
  TCustom,
> {
  taskId: string
  endpoint?: string
  initialCustomState: TCustom
  buildInput: (args: {
    input: string
    messages: TMessage[]
    userMessage: TMessage
  }) => TInput
  createUserMessage: (content: string) => TMessage
  createAssistantMessage: (id: string) => TMessage
  createErrorMessage: (content: string) => TMessage
  appendAssistantDelta: (message: TMessage, delta: string) => TMessage
  reduceEvent?: (
    args: LlmGenerationStreamReducerArgs<TMessage, TCustom>,
  ) => LlmGenerationStreamState<TMessage, TCustom>
  onEvent?: (event: LlmGenerationStreamEvent) => void
  onError?: (error: unknown) => void
  onComplete?: (args: { userInput: string }) => void
}

export function useLlmGenerationStream<
  TInput,
  TMessage extends LlmGenerationClientMessage,
  TCustom,
>(options: UseLlmGenerationStreamOptions<TInput, TMessage, TCustom>) {
  const {
    appendAssistantDelta,
    buildInput,
    createAssistantMessage,
    createErrorMessage,
    createUserMessage,
    endpoint,
    initialCustomState,
    onComplete,
    onError,
    onEvent,
    reduceEvent,
    taskId,
  } = options
  const [state, setState] = useState(() =>
    createInitialLlmGenerationStreamState<TMessage, TCustom>(
      initialCustomState,
    ),
  )
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasReceivedEventsRef = useRef(false)
  const lastSubmittedInputRef = useRef("")

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
    }
  }, [])

  const applyEvent = useCallback(
    (event: LlmGenerationStreamEvent) => {
      onEvent?.(event)
      if (event.event !== "done") {
        hasReceivedEventsRef.current = true
      }

      setState((previousState) => {
        const applyDefault = (
          nextState: LlmGenerationStreamState<TMessage, TCustom>,
        ) =>
          applyDefaultLlmGenerationStreamEvent(nextState, event, {
            initialCustomState,
            createAssistantMessage,
            appendAssistantDelta,
          })

        return reduceEvent
          ? reduceEvent({
              event,
              state: previousState,
              applyDefault,
            })
          : applyDefault(previousState)
      })
    },
    [
      appendAssistantDelta,
      createAssistantMessage,
      initialCustomState,
      onEvent,
      reduceEvent,
    ],
  )

  const setInput = useCallback((input: string) => {
    setState((previousState) => ({
      ...previousState,
      input,
    }))
  }, [])

  const setMessages = useCallback((messages: TMessage[]) => {
    setState((previousState) => ({
      ...previousState,
      messages,
    }))
  }, [])

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setState((previousState) => {
      const nextMessages = hasReceivedEventsRef.current
        ? previousState.messages
        : previousState.messages.filter(
            (message) => message.id !== previousState.activeUserMessageId,
          )
      return {
        ...previousState,
        input: hasReceivedEventsRef.current
          ? previousState.input
          : lastSubmittedInputRef.current,
        status: "idle",
        customState: initialCustomState,
        activeUserMessageId: null,
        streamingMessageId: null,
        startedAt: null,
        messages: nextMessages.map((message) =>
          message.isStreaming ? { ...message, isStreaming: false } : message,
        ),
      }
    })
  }, [initialCustomState])

  const reset = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setState(
      createInitialLlmGenerationStreamState<TMessage, TCustom>(
        initialCustomState,
      ),
    )
  }, [initialCustomState])

  const submit = useCallback(
    async (messageOverride?: string) => {
      const messageToSend = messageOverride ?? state.input
      const trimmedInput = messageToSend.trim()
      if (!trimmedInput) {
        return
      }
      if (state.status === "streaming" || abortControllerRef.current) {
        return
      }

      const userMessage = createUserMessage(trimmedInput)
      const startedAt = Date.now()
      const taskInput = buildInput({
        input: trimmedInput,
        messages: state.messages,
        userMessage,
      })
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      hasReceivedEventsRef.current = false
      lastSubmittedInputRef.current = trimmedInput

      setState((previousState) => ({
        ...previousState,
        messages: [...previousState.messages, userMessage],
        input: "",
        status: "streaming",
        error: null,
        activeUserMessageId: userMessage.id,
        streamingMessageId: null,
        startedAt,
      }))

      try {
        let sawStreamError = false
        let sawDoneEvent = false

        const processEvent = (event: LlmGenerationStreamEvent) => {
          if (abortControllerRef.current !== abortController) {
            return
          }
          if (event.event === "done") {
            sawDoneEvent = true
          }
          if (event.event === "error") {
            sawStreamError = true
          }
          applyEvent(event)
        }

        const completeProcessedEvents = () => {
          if (abortControllerRef.current !== abortController) {
            return
          }

          if (!sawDoneEvent && !sawStreamError) {
            throw new Error(MISSING_DONE_EVENT_MESSAGE)
          }

          setState((previousState) => ({
            ...previousState,
            status: sawStreamError
              ? "error"
              : previousState.status === "streaming"
                ? "idle"
                : previousState.status,
            activeUserMessageId: null,
            streamingMessageId: null,
            startedAt: null,
            messages: previousState.messages.map((message) =>
              message.isStreaming
                ? { ...message, isStreaming: false }
                : message,
            ),
          }))
          if (sawDoneEvent && !sawStreamError) {
            onComplete?.({ userInput: trimmedInput })
          }
        }

        if (!endpoint) {
          if (!isSofiaLlmGenerationTaskId(taskId)) {
            throw new Error(`Unsupported LLM generation stream task: ${taskId}`)
          }

          const result = await runSofiaLlmGenerationJob(
            {
              taskId,
              input: taskInput,
            },
            { signal: abortController.signal },
          )
          for (const event of getSofiaLlmGenerationStreamEvents(result)) {
            processEvent(event)
          }
          completeProcessedEvents()
          return
        }

        const token = await getActiveClerkToken()
        if (!token) {
          throw new Error("Organization authentication token is unavailable")
        }
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            taskId,
            input: taskInput,
          }),
          signal: abortController.signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        if (!response.body) {
          throw new Error("Response body is null")
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ""
        const processDecodedChunk = (chunk: string) => {
          const parsed = appendStreamChunkToBuffer({
            buffer,
            chunk,
          })
          buffer = parsed.buffer
          for (const event of parsed.events) {
            processEvent(event)
          }
        }

        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }

          processDecodedChunk(decoder.decode(value, { stream: true }))
        }

        const tailChunk = decoder.decode()
        if (tailChunk) {
          processDecodedChunk(tailChunk)
        }

        if (buffer.trim()) {
          processDecodedChunk("\n\n")
        }

        completeProcessedEvents()
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") {
          if (abortControllerRef.current !== abortController) {
            return
          }

          setState((previousState) => ({
            ...previousState,
            status: "idle",
            customState: initialCustomState,
            activeUserMessageId: null,
            streamingMessageId: null,
            startedAt: null,
            messages: previousState.messages.map((message) =>
              message.isStreaming
                ? { ...message, isStreaming: false }
                : message,
            ),
          }))
          return
        }

        if (abortControllerRef.current !== abortController) {
          return
        }

        onError?.(error)
        setState((previousState) => ({
          ...previousState,
          status: "error",
          error:
            error instanceof Error
              ? error.message
              : "LLM generation request failed",
          customState: initialCustomState,
          activeUserMessageId: null,
          streamingMessageId: null,
          startedAt: null,
          messages: [
            ...previousState.messages.map((message) =>
              message.isStreaming
                ? { ...message, isStreaming: false }
                : message,
            ),
            createErrorMessage(
              "Sorry, I encountered an error processing your request. Please try again.",
            ),
          ],
        }))
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
      }
    },
    [
      applyEvent,
      buildInput,
      createErrorMessage,
      createUserMessage,
      endpoint,
      initialCustomState,
      onComplete,
      onError,
      state.input,
      state.messages,
      state.status,
      taskId,
    ],
  )

  return useMemo(
    () => ({
      ...state,
      isProcessing: state.status === "streaming",
      setInput,
      setMessages,
      submit,
      cancel,
      reset,
    }),
    [cancel, reset, setInput, setMessages, state, submit],
  )
}
